import React, { useCallback, useEffect, useState } from "react"
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native"
import { ScrollView } from "react-native-gesture-handler"
import Icon from "react-native-vector-icons/Feather"
import { useDispatch, useSelector } from "react-redux"
import { useMediaQuery } from "react-responsive"

import Base, { Actions, Api, Firebase } from "../../base"
import { Alert, ReloadView, TextField } from "../common"
import Gap from "../common/Gap"
import VenueListItem from "./VenueListItem"

const headerOptions = function (showLogout) {
  return {
    ...Base.constants.headerOptions(),
    headerRight: () => (
      <TouchableOpacity
        style={styles.headerButton}
        onPress={() => showLogout()}
      >
        <Icon name="log-out" size={24} color="white" />
      </TouchableOpacity>
    ),
  }
}

export default function VenueSelectorScreen(props) {
  const [error, setError] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const [allVenues, setAllVenues] = useState([])
  const lastVenue = useSelector(state => state.lastVenue)
  const isMobile = useMediaQuery(Base.constants.mobile)
  const dispatch = useDispatch()
  const [searchQuery, setSearchQuery] = useState("")
  const [lastVenueMatch, setLastVenueMatch] = useState(false)

  useEffect(() => {
    getVenues()
  }, [getVenues])
  const getVenues = useCallback(async () => {
    let venues = await Api.getAllVenues()
    setError(venues === null || !Array.isArray(venues) || !venues.length)
    setLoading(false)
    setAllVenues(venues ?? [])
    setLastVenueMatch(
      lastVenue && venues?.map(x => x.id).includes(lastVenue._id),
    )
    const business = await Api.getBusiness()
    if (business && business._id) {
      dispatch(Actions.setBusiness(business))
    }
  }, [dispatch, lastVenue])

  async function logout() {
    await Base.functions.logoutUser(success => {
      Firebase.logEvent("logout_venues")
    })
  }
  props.navigation.setOptions(headerOptions(logout))

  async function venueSelected(venue) {
    setLoading(true)
    const myVenue = await Api.getVenue(venue)
    setLoading(false)
    if (!myVenue) {
      Alert.show(
        "Unable to load Venue",
        "There was a problem retrieving this venue, please check your connection and try again.",
      )
      setLoading(false)
      return
    }
    Firebase.logEvent("venue_selected", { venueId: venue._id })
    dispatch(Actions.setMyVenue(myVenue))
    dispatch(Actions.setLastVenue(myVenue))
    Api.registerForPushMessages()
  }

  function searchResults() {
    if (!searchQuery.length) {
      return allVenues
    }
    return allVenues.filter(
      item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.externalVenueRef
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()),
    )
  }

  function searchQueryChanged(query) {
    setSearchQuery(query || "")
    if (query.length > 2) {
      Firebase.logEvent("venue_search", { query })
    }
  }

  function cancelSearch() {
    setSearchQuery("")
  }

  if (error) {
    return (
      <View style={styles.main}>
        <ReloadView
          title={`Unable to fetch venues,\nplease check your connection.`}
          onPress={getVenues}
        />
      </View>
    )
  }

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Base.colours.appGrey} />
      </View>
    )
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      style={[styles.main, isMobile && styles.main_sm]}
    >
      <Gap size={40} />
      <Text style={isMobile ? Base.styles.title_sm : Base.styles.title_md}>
        Your Venues
      </Text>
      <Gap size={18} />
      <TextField
        style={styles.searchField}
        value={searchQuery}
        placeholder="Search"
        onChangeText={searchQueryChanged}
        rightButton={"Cancel"}
        rightButtonAction={cancelSearch}
        leftIcon={"search"}
        clearButtonMode={"never"}
      />
      {lastVenue && lastVenueMatch ? (
        <View>
          <Text style={styles.sectionTitle}>Most recent selection:</Text>
          <VenueListItem
            delay={100}
            venue={lastVenue}
            onPress={venueSelected}
            isMobile={isMobile}
          />
          <Text style={styles.sectionTitle}>All others:</Text>
        </View>
      ) : (
        <Gap size={20} />
      )}
      {searchResults().map((venue, index) => {
        return (
          <VenueListItem
            delay={100}
            key={index}
            venue={venue}
            onPress={venueSelected}
            isMobile={isMobile}
          />
        )
      })}
      <View style={styles.footer} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  main: {
    flex: 1,
    maxWidth: 450,
    marginHorizontal: Base.constants.hmargin.lg * 0.5,
  },

  main_sm: { marginHorizontal: 16 },

  list_sm: {
    marginHorizontal: Base.constants.hmargin.sm * 0.5,
  },

  list_md: {
    marginHorizontal: Base.constants.hmargin.lg * 0.5,
  },

  footer: {
    height: 50,
  },

  headerButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },

  sectionTitle: {
    marginLeft: 5,
    marginTop: 20,
    marginBottom: 12,
    fontFamily: Base.fonts.appRegular,
  },

  loadingContainer: { flex: 1, justifyContent: "center" },
  searchField: { marginBottom: 5 },
})
